import React, { useState, useRef, useEffect, useCallback } from "react";
import { MapPin } from "lucide-react";

const fieldCls =
  "w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-3.5 text-white font-body placeholder:text-[var(--nl-muted)] focus:outline-none focus:border-gold focus:ring-1 focus:ring-[var(--nl-gold)] transition-colors duration-300";

function formatLabel(p) {
  const line1 = [p.housenumber, p.street].filter(Boolean).join(" ") || p.name;
  return [line1, p.city || p.county, p.state].filter(Boolean).join(", ");
}

// Photon place nodes (cities/towns) should outrank street matches so any Ontario
// city surfaces even when its name collides with a Toronto street (e.g. "Kingston Road").
const PLACE_TYPES = ["city", "town", "village", "municipality", "locality", "hamlet"];
function rankScore(p) {
  if (p.osm_key === "place" && PLACE_TYPES.includes(p.osm_value || p.type)) return 0;
  return 1;
}

// Free address autocomplete via Photon (OpenStreetMap) — no API key.
// Covers all of Ontario (bbox) so clients can order from any city, biased to the populous south.
const ONTARIO_BBOX = "-95.2,41.6,-74.3,57.0"; // minLon,minLat,maxLon,maxLat
export const AddressAutocomplete = ({ value, onChange, onSelect, placeholder, testid, required }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  const search = useCallback((q) => {
    if (!q || q.trim().length < 3) { setSuggestions([]); return; }
    fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=15&lang=en&bbox=${ONTARIO_BBOX}`)
      .then((r) => r.json())
      .then((j) => {
        const feats = (j.features || [])
          .filter((f) => /ontario/i.test(f.properties.state || ""))
          .map((f, i) => ({
            label: formatLabel(f.properties),
            lat: f.geometry.coordinates[1],
            lng: f.geometry.coordinates[0],
            _rank: rankScore(f.properties),
            _i: i,
          }))
          .filter((s) => s.label)
          .sort((a, b) => a._rank - b._rank || a._i - b._i)
          .slice(0, 6);
        setSuggestions(feats);
        setOpen(true);
      })
      .catch(() => setSuggestions([]));
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    onSelect(null); // invalidate coords until a suggestion is chosen
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  };

  const choose = (s) => {
    onChange(s.label);
    onSelect({ label: s.label, lat: s.lat, lng: s.lng });
    setOpen(false);
    setSuggestions([]);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(suggestions[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <input
        required={required}
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length && setOpen(true)}
        className={fieldCls}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={testid}
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-[1000] left-0 right-0 mt-1 bg-[var(--nl-bg-3)] border border-gold/30 max-h-64 overflow-auto shadow-2xl" data-testid={`${testid}-suggestions`}>
          {suggestions.map((s, i) => (
            <li
              key={`${s.lat}-${s.lng}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); choose(s); }}
              onMouseEnter={() => setActive(i)}
              className={`flex items-start gap-2 px-4 py-3 cursor-pointer font-body text-sm transition-colors duration-150 ${i === active ? "bg-gold/15 text-white" : "text-white/70 hover:bg-white/5"}`}
              data-testid={`${testid}-option-${i}`}
            >
              <MapPin size={14} className="text-gold mt-0.5 shrink-0" />
              <span>{s.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
